import { useState } from "react";
import {
  Download,
  User,
  Phone,
  MapPin,
  Calendar,
  Bus,
  Sparkles
} from "lucide-react";
import emailjs from "@emailjs/browser";

import { generatePDF } from "../utils/pdfGenerator";
import { transportData } from "../components/TransportData";
import { transportDataGaza } from "../components/TransportDataGaza";

// EmailJS config
const EMAILJS_PUBLIC_KEY = "W-CL6ETbw0q5XEFYz";
const EMAILJS_SERVICE_ID = "service_nh9y863";
const EMAILJS_TEMPLATE_ID = "template_5avy99o";

const BookingForm = ({ onBookingComplete }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    cellphone: "",
    transportRoute: "",
    pickupPoint: "",
    dropoffLocation: "",
    travelDate: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | "success" | "error"

  // Pick the active data structure based on the current selection state
  let activeTransport = transportData; // default fallback

  if (formData.transportRoute === "Gaza → Vaal") {
    activeTransport = transportDataGaza;
  } else if (formData.transportRoute === "Vaal → JHB") {
    activeTransport = transportData;
  } else if (formData.transportRoute === "Vaal → Venda") {
    activeTransport = transportData;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };

      // Clear dependent fields if the route itself is changed to prevent invalid combinations
      if (name === "transportRoute") {
        updatedData.pickupPoint = "";
        updatedData.dropoffLocation = "";
        updatedData.travelDate = "";
      }

      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setEmailStatus(null);

    const bookingReference = `V2V-${Date.now().toString().slice(-8)}`;

    const bookingDetails = {
      ...formData,
      reference: bookingReference,
      totalPrice: `R${activeTransport.pricing?.totalPrice || 0}`,
      deposit: `R${activeTransport.pricing?.deposit || 0}`,
      bookingDate: new Date().toLocaleDateString("en-ZA")
    };

    // Send booking notification email via EmailJS
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          fullName: bookingDetails.fullName,
          cellphone: bookingDetails.cellphone,
          transportRoute: bookingDetails.transportRoute,
          pickupPoint: bookingDetails.pickupPoint,
          dropoffLocation: bookingDetails.dropoffLocation,
          travelDate: bookingDetails.travelDate,
          reference: bookingDetails.reference,
          totalPrice: bookingDetails.totalPrice,
          deposit: bookingDetails.deposit,
          bookingDate: bookingDetails.bookingDate
        },
        EMAILJS_PUBLIC_KEY
      );
      setEmailStatus("success");
    } catch (error) {
      console.error("Failed to send booking email:", error);
      setEmailStatus("error");
      // Don't block the PDF download if email fails
    }

    // Generate and download the PDF receipt (unchanged behavior)
    generatePDF(bookingDetails, activeTransport);

    if (onBookingComplete) {
      onBookingComplete(bookingDetails);
    }

    setIsSubmitting(false);
  };

  const isFormValid =
    formData.fullName &&
    formData.cellphone &&
    formData.transportRoute &&
    formData.pickupPoint &&
    formData.dropoffLocation &&
    formData.travelDate;

  const inputStyle = `
    w-full
    h-14
    px-5
    rounded-xl
    border
    border-gray-200
    bg-white
    outline-none
    focus:ring-4
    focus:ring-blue-100
    focus:border-blue-500
  `;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100"
    >
      {/* HEADER */}
      <div className="text-center mb-10">
        <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center">
          <Bus className="text-white" />
        </div>
        <h2 className="text-3xl font-bold">Book Your Seat</h2>
        <p className="text-gray-500 mt-2">Complete your details below</p>
      </div>

      {/* NAME */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <User size={18} />
          Full Names *
        </label>
        <input
          className={inputStyle}
          name="fullName"
          placeholder="Enter your full name"
          value={formData.fullName}
          onChange={handleChange}
        />
      </div>

      {/* PHONE */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Phone size={18} />
          Cellphone Number *
        </label>
        <input
          className={inputStyle}
          name="cellphone"
          placeholder="Enter cellphone number"
          value={formData.cellphone}
          onChange={handleChange}
        />
      </div>

      {/* TRANSPORT ROUTE */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Bus size={18} />
          Which transport Route Are You Looking For? *
        </label>
        <select
          className={inputStyle}
          name="transportRoute"
          value={formData.transportRoute}
          onChange={handleChange}
        >
          <option value="">Select transport</option>
          {(transportData.pickupTransport || []).map((route) => (
            <option key={route} value={route}>
              {route}
            </option>
          ))}
        </select>
      </div>

      {/* PICKUP */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <MapPin size={18} />
          Pickup Point *
        </label>
        <select
          className={inputStyle}
          name="pickupPoint"
          value={formData.pickupPoint}
          onChange={handleChange}
          disabled={!formData.transportRoute}
        >
          <option value="">Select pickup point</option>
          {(activeTransport.pickupPoints || []).map((point) => (
            <option key={point} value={point}>
              {point}
            </option>
          ))}
        </select>
      </div>

      {/* DROP OFF */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-3">
          <MapPin size={18} />
          Drop-off Location *
        </label>
        <div className="space-y-3">
          {formData.transportRoute ? (
            (activeTransport.dropoffLocations || []).map((location) => (
              <label
                key={location}
                className={`
                  flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition
                  ${
                    formData.dropoffLocation === location
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200"
                  }
                `}
              >
                <input
                  type="radio"
                  name="dropoffLocation"
                  value={location}
                  checked={formData.dropoffLocation === location}
                  onChange={handleChange}
                />
                <span>{location}</span>
              </label>
            ))
          ) : (
            <p className="text-sm text-gray-400 italic">Please select a transport route first.</p>
          )}
        </div>
      </div>

      {/* DATE */}
      <div className="mb-8">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Calendar size={18} />
          Travel Date *
        </label>
        <select
          className={inputStyle}
          name="travelDate"
          value={formData.travelDate}
          onChange={handleChange}
          disabled={!formData.transportRoute}
        >
          <option value="">Select date</option>
          {(activeTransport.travelDates || []).map((date) => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
        </select>
      </div>

      {/* PRICE SUMMARY */}
      <div className="bg-blue-50 rounded-2xl p-6 mb-8">
        <div className="flex gap-2 font-bold mb-5">
          <Sparkles size={18} />
          Price Summary
        </div>
        <div className="flex justify-between mb-3">
          <span>Total Price</span>
          <strong className="text-xl">
            R{activeTransport.pricing?.totalPrice || 0}
          </strong>
        </div>
        <div className="flex justify-between">
          <span>Deposit Required</span>
          <strong className="text-blue-600 text-xl">
            R{activeTransport.pricing?.deposit || 0}
          </strong>
        </div>
      </div>

      {/* EMAIL STATUS FEEDBACK */}
      {emailStatus === "success" && (
        <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
          
        </div>
      )}
      {emailStatus === "error" && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm">
          Your receipt downloaded, but the booking notification email failed to send.
        </div>
      )}

      {/* BUTTON */}
      <button
        disabled={!isFormValid || isSubmitting}
        className={`
          w-full h-14 rounded-xl font-bold flex items-center justify-center gap-3 transition
          ${
            isFormValid && !isSubmitting
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }
        `}
      >
        {isSubmitting ? (
          "Processing..."
        ) : (
          <>
            <Download size={20} />
            Submit & Download Receipt
          </>
        )}
      </button>
    </form>
  );
};

export default BookingForm;
